import api from "@/lib/apiClient";

/**
 * UPLOAD-ACK-1 fix-4 — THE CLIENT IDEMPOTENCY TOKEN, defined once.
 *
 * A 74 MB upload that times out mid-stream left the teacher with a failure toast and the server with
 * a lesson it had already created. Retrying blind made a SECOND lesson. The token lets the server's
 * early key-dedup recognise the retry and return the lesson it already has, cheaply, before it
 * re-reads the bytes.
 *
 * STABLE PER ATTEMPT, NOT PER REQUEST. That is the whole contract: a caller mints one key when the
 * teacher picks a file and sends THE SAME key on every retry of that file. A key minted inside the
 * request would be fresh on each retry and would defeat the dedup it exists to enable — so this
 * module exports the minting function and deliberately does NOT inject a key itself. A call site
 * that forgets sends no key and gets today's behaviour: an absence, not a misleading presence.
 *
 * randomUUID is absent in some test and older browser environments, hence the fallback — it only
 * needs to be unique per client attempt, never unguessable.
 */
export const newUploadKey = () => {
  const uuid = globalThis.crypto && globalThis.crypto.randomUUID;
  if (typeof uuid === "function") return globalThis.crypto.randomUUID();
  return `uk-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

/**
 * UPLOAD-ACK-1 fix-4 — a network failure is NOT an upload failure until the retry has been tried.
 *
 * TRUE ONLY WHEN NO RESPONSE CAME BACK. A server that answered 4xx or 5xx has made a decision, and
 * re-sending 74 MB at it is pointless at best; the case worth retrying is the one where we never
 * learned the outcome, which is exactly when the lesson may already exist.
 */
export const isNoResponseError = (error) => Boolean(error) && !error.response;

export const videoApi = {
  upload: (formData, config = {}) =>
    api.post("/api/videos/upload", formData, {
      // Large lesson videos must not be aborted by the shared 30s client
      // default while bytes are still streaming. Disable the timeout for the
      // upload request ONLY (per-request; the shared apiClient default is
      // unchanged for every other call). Callers pass onUploadProgress, so the
      // UI keeps showing progress — no silent infinite hang.
      timeout: 0,
      ...config,
    }),
  list: (params) => api.get("/api/videos", { params }),
  status: (videoId) => api.get(`/api/videos/${videoId}/status`),
  detail: (videoId) => api.get(`/api/videos/${videoId}`),
  // T8/T6: the one-way teacher Share control — set a lesson's per-lesson visibility.
  // Owner-only; only "shared_with_coach" is accepted (unshare is rejected server-side).
  updateVisibility: (videoId, payload) => api.patch(`/api/videos/${videoId}/visibility`, payload),
  // ADMIN-A8: owner edit of a lesson's label (title/subject/class) — accepts a timetable
  // suggestion in one tap or a hand-typed title. Owner-only, server-enforced.
  editLesson: (videoId, payload) => api.patch(`/api/videos/${videoId}`, payload),
  // DELETE-1 — total, residue-free deletion of the teacher's own lesson (owner-only, server-enforced).
  deleteLesson: (videoId) => api.delete(`/api/videos/${videoId}`),
  retry: (videoId) => api.post(`/api/videos/${videoId}/retry`),
  retryPrivacy: (videoId) => api.post(`/api/videos/${videoId}/privacy/retry`),
  analysisMoments: (videoId) => api.get(`/api/admin/videos/${videoId}/analysis-moments`),
  comments: (videoId) => api.get(`/api/videos/${videoId}/comments`),
  createComment: (videoId, payload) => api.post(`/api/videos/${videoId}/comments`, payload),
  updateComment: (videoId, commentId, payload) =>
    api.patch(`/api/videos/${videoId}/comments/${commentId}`, payload),
  deleteComment: (videoId, commentId) => api.delete(`/api/videos/${videoId}/comments/${commentId}`),
  audioAnalysis: (videoId) => api.get(`/api/videos/${videoId}/audio-analysis`),
  // PR C9.5 PART 4 / PART 6 — corrective-action endpoints behind the eligibility map.
  runAudioAnalysis: (videoId) => api.post(`/api/videos/${videoId}/audio/run`),
  reprojectFeedback: (videoId) => api.post(`/api/videos/${videoId}/feedback/reproject`),
};

export const evidenceApi = {
  get: (assessmentId) => api.get(`/api/assessments/${assessmentId}/evidence`),
};
